import {
  type DocumentStatus,
  ContentType,
  Role,
  SourceType,
  WebsitePageStatus,
} from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button, cn, Dropdown } from '@utima/ui';
import {
  CircleDot,
  CircleX,
  ChartNoAxesColumnIncreasing,
  Cloud,
  DatabaseBackup,
  Download,
  Eye,
  FileSymlink,
  FileText,
  FileX2,
  FlaskConical,
  Folder,
  Globe,
  GlobeLock,
  Link,
  Lock,
  MoreHorizontal,
  Pencil,
  Rss,
  Scissors,
  Shield,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useMemo, type SyntheticEvent } from 'react';

import { DropdownIconMenuItem, LockedFeatureTooltip } from '@/components';
import { IconWrapper } from '@/components/iconWrapper/IconWrapper';
import { Protected } from '@/components/protected/Protected';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { DocumentProcessingStatus } from '@/features/documents/components/DocumentProcessingStatus';
import { useEnumTranslations } from '@/hooks';

import { useKBWebsiteHierarchyLabels } from './useKBWebsiteHierarchyLabels';
import { ContentUpdatedCell } from '../components/ContentUpdatedCell';
import {
  useFolderDefaultPriorityTooltip,
  useKnowledgePriorityHeaderTooltip,
} from '../components/FolderDefaultPriorityHint';
import { InlinePriorityEditor } from '../components/InlinePriorityEditor';
import type {
  KBContentRow,
  KBDocumentData,
  KBExcludeFromKBTarget,
} from '../components/kbTypes';
import { PriorityIndicator } from '../components/PriorityIndicator';
import {
  getKBRowCutState,
  getRowDestructiveAction,
  canExcludeRow,
  type KBRowActionContext,
} from '../lib/websiteHierarchy';

function getFolderIcon(folder: {
  sourceType?: SourceType | string | null;
  sourceUri?: string | null;
  websitePathPrefix?: string | null;
}) {
  if (folder.sourceType === SourceType.Rss) return Rss;
  if (folder.sourceType === SourceType.GoogleDrive) return Cloud;

  if (folder.sourceUri) {
    return folder.websitePathPrefix ? Globe : GlobeLock;
  }

  return Folder;
}

function getDocumentIcon(type: string) {
  if (type === ContentType.Document) return FileText;
  if (type === ContentType.Website) return Globe;

  return FileSymlink;
}

function getDocumentIconColor(type: string) {
  if (type === ContentType.Document) return 'text-danger';
  if (type === ContentType.Website) return 'text-primary';

  return 'text-amber-500';
}

function getPageStatusColor(status: string) {
  if (status === WebsitePageStatus.New) return 'text-amber-500';
  if (status === WebsitePageStatus.Removed) return 'text-danger';
  if (status === WebsitePageStatus.Untracked) return 'text-gray-400';

  return 'text-success';
}

const columnHelper = createColumnHelper<KBContentRow>();

export type KBContentColumnActions = {
  onExportDocuments?: (documentIds: string[]) => void;
  onOpenDocumentTab: (documentId: string, tab: string) => void;
  onViewDetails?: (documentId: string) => void;
  onDeleteDocument: (documentId: string) => void;
  onDeleteRemovedPages?: (pageIds: string[]) => void;
  onRequestDeleteFolder: (folderId: string) => void;
  onExcludeFromKB?: (target: KBExcludeFromKBTarget) => void;
  onFolderSettings: (
    folderId: string,
    tab?: 'general' | 'auto-update' | 'agents',
  ) => void;
  onCutItem?: (id: string, type: 'folder' | 'document') => void;
  onDismissPage?: (pageId: string) => void;
  onIncludePage?: (pageId: string) => void;
  onRefreshDocument: (documentId: string) => void;
  onResetEmbedding: (documentId: string) => void;
  onGenerateTestCases: (documentId: string) => void;
  onNavigateToFolder?: (folderId: string) => void;
  onUpdateDocumentPriority?: (
    documentId: string,
    weight: number,
  ) => Promise<void>;
  onUpdateFolderPriority?: (folderId: string, weight: number) => Promise<void>;
};

export type UseKBContentColumnsOptions = {
  canUseAutoUpdate?: boolean;
  isManagedSource?: boolean;
  isChatMode?: boolean;
};

export function useKBContentColumns(
  actions?: KBContentColumnActions,
  folderPaths?: Record<string, string> | null,
  options: UseKBContentColumnsOptions = {},
) {
  const {
    canUseAutoUpdate = true,
    isManagedSource = false,
    isChatMode = false,
  } = options;
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const folderDefaultPriorityTooltip = useFolderDefaultPriorityTooltip();
  const knowledgePriorityHeaderTooltip = useKnowledgePriorityHeaderTooltip();
  const websiteHierarchyLabels = useKBWebsiteHierarchyLabels();

  return useMemo(
    () => [
      // Column 1: Type icon
      columnHelper.display({
        id: 'type',
        header: '',
        meta: { size: 48, align: 'center' },
        cell: ({ row }) => {
          const item = row.original;

          if (item.rowType === 'folder') {
            const folder = item.data;
            const isManagedSourceRoot = !!folder.sourceUri;

            return (
              <IconWrapper
                size='xs'
                className={
                  isManagedSourceRoot ? 'text-primary' : 'text-amber-600'
                }
                Icon={getFolderIcon(folder)}
              />
            );
          }

          if (item.rowType === 'document') {
            const docType = String(item.data.type);

            return (
              <IconWrapper
                size='xs'
                className={getDocumentIconColor(docType)}
                Icon={getDocumentIcon(docType)}
              />
            );
          }

          // page
          return (
            <CircleDot
              className={cn(
                'size-5',
                getPageStatusColor(String(item.data.status)),
              )}
            />
          );
        },
      }),

      // Column 2: Name + description
      columnHelper.display({
        id: 'name',
        header: t`Name`,
        meta: { sortable: 'name' },
        cell: ({ row }) => {
          const item = row.original;

          if (item.rowType === 'folder') {
            const folder = item.data;
            const pathLabel = folderPaths?.[folder.id];
            const parentFolderId =
              folder.parent && typeof folder.parent === 'object'
                ? (folder.parent as { id: string }).id
                : (folder.parent as string | undefined);
            // RSS folders default their name to the feed's own <title>, which
            // is often generic ("RSS 2.0 Feed") — the URL is the reliable
            // identity anchor, so show it as the primary label and keep the
            // parsed title as a secondary line.
            const isRssFeed =
              folder.sourceType === SourceType.Rss && !!folder.sourceUri;
            const primaryLabel = isRssFeed ? folder.sourceUri : folder.name;
            const feedTitle =
              isRssFeed && folder.name !== folder.sourceUri
                ? folder.name
                : undefined;

            return (
              <div className='flex min-w-48 flex-col gap-0.5'>
                <p
                  className='truncate font-medium'
                  title={primaryLabel ?? undefined}
                >
                  {primaryLabel}
                </p>
                {feedTitle && (
                  <p
                    className='truncate text-xs text-muted-fg'
                    title={feedTitle}
                  >
                    {feedTitle}
                  </p>
                )}
                {pathLabel ? (
                  <button
                    type='button'
                    className='w-fit text-left text-xs text-muted-fg hover:text-primary hover:underline'
                    onClick={e => {
                      e.stopPropagation();

                      if (parentFolderId && actions?.onNavigateToFolder) {
                        actions.onNavigateToFolder(parentFolderId);
                      }
                    }}
                  >
                    📁 {pathLabel}
                  </button>
                ) : (
                  folder.description && (
                    <p className='text-xs text-muted-fg'>
                      {folder.description.length > 80
                        ? folder.description.substring(0, 80) + '...'
                        : folder.description}
                    </p>
                  )
                )}
              </div>
            );
          }

          if (item.rowType === 'document') {
            const doc = item.data as KBDocumentData;
            const desc = doc.description || doc.shortSummary;
            const hasFlatFolderContext = !!folderPaths;
            const docFolderId =
              doc.folder && typeof doc.folder === 'object'
                ? doc.folder.id
                : undefined;
            const pathLabel = docFolderId
              ? folderPaths?.[docFolderId]
              : undefined;
            const folderName =
              hasFlatFolderContext &&
              !pathLabel &&
              doc.folder &&
              typeof doc.folder === 'object'
                ? doc.folder.name
                : undefined;

            return (
              <div className='flex min-w-48 flex-col gap-0.5'>
                <p className='break-all font-medium leading-tight'>
                  {doc.name}
                </p>
                {hasFlatFolderContext && (pathLabel || folderName) && (
                  <button
                    type='button'
                    className='w-fit text-left text-xs text-muted-fg hover:text-primary hover:underline'
                    onClick={e => {
                      e.stopPropagation();

                      if (docFolderId && actions?.onNavigateToFolder) {
                        actions.onNavigateToFolder(docFolderId);
                      }
                    }}
                  >
                    📁 {pathLabel ?? folderName}
                  </button>
                )}
                {!pathLabel && !folderName && desc && (
                  <p className='text-xs text-muted-fg'>
                    {desc.length > 80 ? desc.substring(0, 80) + '...' : desc}
                  </p>
                )}
              </div>
            );
          }

          // page
          {
            const pageFolderId =
              item.data.folder && typeof item.data.folder === 'object'
                ? (item.data.folder as { id: string }).id
                : undefined;
            const pagePathLabel = pageFolderId
              ? folderPaths?.[pageFolderId]
              : undefined;

            return (
              <div className='flex min-w-48 flex-col gap-0.5'>
                <p className='break-all font-medium leading-tight'>
                  {item.data.url}
                </p>
                {pagePathLabel && (
                  <button
                    type='button'
                    className='w-fit text-left text-xs text-muted-fg hover:text-primary hover:underline'
                    onClick={e => {
                      e.stopPropagation();

                      if (pageFolderId && actions?.onNavigateToFolder) {
                        actions.onNavigateToFolder(pageFolderId);
                      }
                    }}
                  >
                    📁 {pagePathLabel}
                  </button>
                )}
              </div>
            );
          }
        },
      }),

      // Column 3: Status
      columnHelper.display({
        id: 'status',
        header: t`Status`,
        cell: ({ row }) => {
          const item = row.original;

          if (item.rowType === 'folder') return null;

          if (item.rowType === 'document') {
            const doc = item.data as KBDocumentData;

            return (
              <DocumentProcessingStatus
                status={doc.status}
                label={
                  enumTranslations.documentStatus[doc.status as DocumentStatus]
                }
                pdfProgress={doc.pdfProgress}
              />
            );
          }

          // page
          const status = item.data.status as WebsitePageStatus;

          return (
            <Badge
              size='sm'
              variant={
                status === WebsitePageStatus.New
                  ? 'warning'
                  : status === WebsitePageStatus.Removed
                    ? 'danger'
                    : status === WebsitePageStatus.Existing
                      ? 'success'
                      : 'secondary'
              }
              outline
            >
              {enumTranslations.websitePageStatus[status]}
            </Badge>
          );
        },
      }),

      columnHelper.display({
        id: 'updated',
        header: t`Content updated`,
        cell: ({ row }) => {
          const item = row.original;
          const doc =
            item.rowType === 'document'
              ? (item.data as KBDocumentData)
              : undefined;

          return (
            <ContentUpdatedCell
              contentUpdatedAt={doc?.contentUpdatedAt}
              createdAt={item.data.createdAt}
              lastCheckedAt={doc?.lastCheckedAt}
            />
          );
        },
      }),

      // Column 5: Priority
      columnHelper.display({
        id: 'priority',
        header: t`Priority`,
        meta: {
          align: 'left',
          size: 160,
          headerTooltip: knowledgePriorityHeaderTooltip,
        },
        cell: ({ row }) => {
          const item = row.original;

          if (item.rowType === 'page') return '—';

          const weight =
            item.rowType === 'folder'
              ? item.data.defaultWeight
              : (item.data as KBDocumentData).weight;
          const resolvedWeight = weight ?? 0.5;

          if (item.rowType === 'folder' && actions?.onUpdateFolderPriority) {
            return (
              <InlinePriorityEditor
                weight={resolvedWeight}
                variant='muted'
                onChange={nextWeight =>
                  actions.onUpdateFolderPriority!(item.id, nextWeight)
                }
              />
            );
          }

          if (
            item.rowType === 'document' &&
            actions?.onUpdateDocumentPriority
          ) {
            return (
              <InlinePriorityEditor
                weight={resolvedWeight}
                onChange={nextWeight =>
                  actions.onUpdateDocumentPriority!(item.id, nextWeight)
                }
              />
            );
          }

          if (item.rowType === 'folder') {
            return (
              <SimpleTooltip title={folderDefaultPriorityTooltip}>
                <span className='inline-flex'>
                  <PriorityIndicator weight={resolvedWeight} variant='muted' />
                </span>
              </SimpleTooltip>
            );
          }

          return <PriorityIndicator weight={resolvedWeight} />;
        },
      }),

      // Column 6: Agents
      columnHelper.display({
        id: 'agents',
        header: t`Agents`,
        meta: { size: 150 },
        cell: ({ row }) => {
          const item = row.original;

          if (item.rowType === 'page') return null;

          if (item.rowType === 'folder') {
            const folder = item.data;

            if (folder.availableForAllAgents) {
              return (
                <Badge size='xs' variant='success' outline>
                  All
                </Badge>
              );
            }

            const chats = folder.chats;

            if (!chats || (Array.isArray(chats) && chats.length === 0)) {
              return <span className='text-xs text-muted-fg'>—</span>;
            }

            return (
              <span className='text-xs text-muted-fg'>
                {Array.isArray(chats) ? chats.length : 0}
              </span>
            );
          }

          // document
          const doc = item.data as KBDocumentData;

          if (doc.effectiveAvailableForAllAgents ?? doc.availableForAllAgents) {
            return (
              <Badge size='xs' variant='success' outline>
                All
              </Badge>
            );
          }

          const effectiveCount = doc.effectiveChatIds?.length ?? 0;
          const directChats = doc.chats;
          const directCount = directChats?.length ?? 0;
          const hasInherited = effectiveCount > directCount;

          if (effectiveCount === 0 && directCount === 0) {
            return <span className='text-xs text-muted-fg'>—</span>;
          }

          return (
            <span
              className='text-xs text-muted-fg'
              title={
                hasInherited
                  ? t`${directCount} direct, ${effectiveCount - directCount} inherited from folder`
                  : undefined
              }
            >
              {effectiveCount > 0 ? effectiveCount : directCount}
              {hasInherited && (
                <span className='ml-0.5 text-primary/60'>↑</span>
              )}
            </span>
          );
        },
      }),

      // Column 8: Row actions (⋯ menu)
      ...(actions
        ? [
            columnHelper.display({
              id: 'actions',
              header: '',
              meta: { size: 48, align: 'center' },
              cell: ({ row }) => {
                const item = row.original;

                const stopRowActionEvent = (event: SyntheticEvent) => {
                  event.stopPropagation();
                };

                const rowActionWrapperProps = {
                  'data-kb-row-action': true,
                  onClick: stopRowActionEvent,
                  onDoubleClick: stopRowActionEvent,
                  onPointerDown: stopRowActionEvent,
                };

                if (item.rowType === 'page') {
                  if (
                    item.data.status !== WebsitePageStatus.Removed ||
                    !actions.onDeleteRemovedPages
                  )
                    return null;

                  return (
                    <div {...rowActionWrapperProps}>
                      <Dropdown.Root modal={false}>
                        <Dropdown.Trigger asChild>
                          <Button
                            variant='ghost'
                            size='icon-xs'
                            icon={<MoreHorizontal size={16} />}
                          />
                        </Dropdown.Trigger>
                        <Dropdown.Content side='bottom' align='end'>
                          <DropdownIconMenuItem
                            icon={Trash2}
                            className='text-danger'
                            label={<Trans>Delete</Trans>}
                            onClick={() =>
                              actions.onDeleteRemovedPages?.([item.id])
                            }
                          />
                        </Dropdown.Content>
                      </Dropdown.Root>
                    </div>
                  );
                }

                const isDocument = item.rowType === 'document';
                const isFolder = item.rowType === 'folder';
                const isWebsiteDoc =
                  isDocument &&
                  (item.data as KBDocumentData).type === ContentType.Website;

                const actionContext: KBRowActionContext = {
                  isChatMode,
                  isManagedSource,
                };
                const cutState = getKBRowCutState(
                  item,
                  actionContext,
                  websiteHierarchyLabels,
                );
                const destructiveAction = getRowDestructiveAction(
                  item,
                  actionContext,
                );
                const showDocumentCut = isDocument && !!actions.onCutItem;
                const showFolderCut = isFolder && !!actions.onCutItem;

                const removeFromTrackedLabel =
                  websiteHierarchyLabels.removeFromTrackedPagesLabel;
                const removeFromTrackedDisclaimer =
                  websiteHierarchyLabels.removeFromTrackedPagesDisclaimer;

                return (
                  <div
                    {...rowActionWrapperProps}
                    className='flex items-center justify-center gap-1'
                  >
                    {isDocument && actions.onViewDetails && (
                      <SimpleTooltip title={t`View details`}>
                        <Button
                          variant='ghost'
                          size='icon-xs'
                          icon={<Eye size={16} />}
                          onClick={() => actions.onViewDetails!(item.id)}
                        />
                      </SimpleTooltip>
                    )}
                    <Dropdown.Root modal={false}>
                      <Dropdown.Trigger asChild>
                        <Button
                          variant='ghost'
                          size='icon-xs'
                          icon={<MoreHorizontal size={16} />}
                        />
                      </Dropdown.Trigger>
                      <Dropdown.Content side='bottom' align='end'>
                        {showDocumentCut && (
                          <DropdownIconMenuItem
                            icon={Scissors}
                            label={<Trans>Cut</Trans>}
                            disabled={!cutState.enabled}
                            disabledTooltip={cutState.disabledTooltip}
                            onClick={
                              cutState.enabled
                                ? () => actions.onCutItem?.(item.id, 'document')
                                : undefined
                            }
                          />
                        )}

                        {isDocument && actions.onExportDocuments && (
                          <Dropdown.Item
                            className='flex items-center gap-2'
                            onClick={e => {
                              e.stopPropagation();
                              actions.onExportDocuments?.([item.id]);
                            }}
                          >
                            <Download className='size-4' />
                            <Trans>Download / export</Trans>
                          </Dropdown.Item>
                        )}

                        {isDocument && (
                          <Dropdown.Item
                            className='flex items-center gap-2'
                            onClick={e => {
                              e.stopPropagation();
                              actions.onOpenDocumentTab(item.id, 'agents');
                            }}
                          >
                            <Shield className='size-4' />
                            <Trans>Agent access</Trans>
                          </Dropdown.Item>
                        )}

                        {isDocument && isWebsiteDoc && (
                          <LockedFeatureTooltip locked={!canUseAutoUpdate}>
                            <Dropdown.Item
                              className='flex items-center gap-2'
                              onClick={e => {
                                e.stopPropagation();
                                actions.onOpenDocumentTab(
                                  item.id,
                                  'auto-update',
                                );
                              }}
                            >
                              {canUseAutoUpdate ? (
                                <RefreshCw className='size-4' />
                              ) : (
                                <Lock className='size-4' />
                              )}
                              <Trans>Auto-update Settings</Trans>
                            </Dropdown.Item>
                          </LockedFeatureTooltip>
                        )}

                        {isDocument && (
                          <Dropdown.Item
                            className='flex items-center gap-2'
                            onClick={e => {
                              e.stopPropagation();
                              actions.onRefreshDocument(item.id);
                            }}
                            title={
                              item.data.sourceType === SourceType.Rss
                                ? t`Refresh data reprocesses these entries. Use Sync now on the feed to fetch new or updated feed content.`
                                : undefined
                            }
                          >
                            <DatabaseBackup className='size-4' />
                            <Trans>Refresh data</Trans>
                          </Dropdown.Item>
                        )}

                        <Protected roles={[Role.SuperAdmin]}>
                          {isDocument && (
                            <Dropdown.Item
                              className='flex items-center gap-2'
                              onClick={e => {
                                e.stopPropagation();
                                actions.onResetEmbedding(item.id);
                              }}
                            >
                              <FileX2 className='size-4' />
                              <Trans>Reset embeddings</Trans>
                            </Dropdown.Item>
                          )}
                        </Protected>

                        {isDocument &&
                          (item.data as KBDocumentData).publicUrl && (
                            <Dropdown.Item
                              className='flex items-center gap-2'
                              asChild
                            >
                              <a
                                href={(item.data as KBDocumentData).publicUrl!}
                                target='_blank'
                                rel='noreferrer'
                                onClick={e => e.stopPropagation()}
                              >
                                <Link className='size-4' />
                                <Trans>Public URL</Trans>
                              </a>
                            </Dropdown.Item>
                          )}

                        <Protected roles={[Role.SuperAdmin]}>
                          {isDocument && (
                            <Dropdown.Item
                              className='flex items-center gap-2'
                              onClick={e => {
                                e.stopPropagation();
                                actions.onGenerateTestCases(item.id);
                              }}
                            >
                              <FlaskConical className='size-4' />
                              <Trans>Generate test cases</Trans>
                            </Dropdown.Item>
                          )}
                        </Protected>

                        {isDocument && destructiveAction && (
                          <Dropdown.Separator />
                        )}

                        {isDocument &&
                          canExcludeRow(item, actionContext) &&
                          !!actions.onExcludeFromKB && (
                            <DropdownIconMenuItem
                              icon={CircleX}
                              label={removeFromTrackedLabel}
                              hint={
                                destructiveAction === 'exclude'
                                  ? removeFromTrackedDisclaimer
                                  : undefined
                              }
                              onClick={() =>
                                actions.onExcludeFromKB?.({
                                  documentIds: [item.id],
                                })
                              }
                            />
                          )}

                        {isDocument && destructiveAction && (
                          <DropdownIconMenuItem
                            icon={Trash2}
                            className='text-danger'
                            label={<Trans>Delete</Trans>}
                            disabled={destructiveAction !== 'delete'}
                            disabledTooltip={removeFromTrackedDisclaimer}
                            onClick={() => actions.onDeleteDocument(item.id)}
                          />
                        )}

                        {isFolder && (
                          <>
                            <Dropdown.Item
                              className='flex items-center gap-2'
                              onClick={e => {
                                e.stopPropagation();
                                actions.onFolderSettings(item.id, 'general');
                              }}
                            >
                              <Pencil className='size-4' />
                              <Trans>Edit</Trans>
                            </Dropdown.Item>

                            {showFolderCut && (
                              <DropdownIconMenuItem
                                icon={Scissors}
                                label={<Trans>Cut</Trans>}
                                disabled={!cutState.enabled}
                                disabledTooltip={cutState.disabledTooltip}
                                onClick={
                                  cutState.enabled
                                    ? () =>
                                        actions.onCutItem?.(item.id, 'folder')
                                    : undefined
                                }
                              />
                            )}

                            <Dropdown.Item
                              className='flex items-center gap-2'
                              onClick={e => {
                                e.stopPropagation();
                                actions.onFolderSettings(item.id, 'agents');
                              }}
                            >
                              <Shield className='size-4' />
                              <Trans>Agent access</Trans>
                            </Dropdown.Item>

                            {isFolder && (
                              <LockedFeatureTooltip locked={!canUseAutoUpdate}>
                                <Dropdown.Item
                                  className='flex items-center gap-2'
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (canUseAutoUpdate) {
                                      actions.onFolderSettings(
                                        item.id,
                                        'auto-update',
                                      );
                                    }
                                  }}
                                  disabled={!canUseAutoUpdate}
                                >
                                  {canUseAutoUpdate ? (
                                    <RefreshCw className='size-4' />
                                  ) : (
                                    <Lock className='size-4' />
                                  )}
                                  <Trans>Auto-update Settings</Trans>
                                </Dropdown.Item>
                              </LockedFeatureTooltip>
                            )}

                            <Dropdown.Item
                              className='flex items-center gap-2'
                              onClick={e => {
                                e.stopPropagation();
                                actions.onFolderSettings(item.id, 'general');
                              }}
                            >
                              <ChartNoAxesColumnIncreasing className='size-4' />
                              <Trans>Priority</Trans>
                            </Dropdown.Item>

                            {destructiveAction && <Dropdown.Separator />}

                            {destructiveAction === 'exclude' &&
                              !!actions.onExcludeFromKB && (
                                <DropdownIconMenuItem
                                  icon={CircleX}
                                  label={removeFromTrackedLabel}
                                  hint={removeFromTrackedDisclaimer}
                                  onClick={() =>
                                    actions.onExcludeFromKB?.({
                                      folderIds: [item.id],
                                    })
                                  }
                                />
                              )}

                            {destructiveAction && (
                              <DropdownIconMenuItem
                                icon={Trash2}
                                className='text-danger'
                                label={<Trans>Delete</Trans>}
                                disabled={destructiveAction !== 'delete'}
                                disabledTooltip={removeFromTrackedDisclaimer}
                                onClick={() =>
                                  actions.onRequestDeleteFolder(item.id)
                                }
                              />
                            )}
                          </>
                        )}
                      </Dropdown.Content>
                    </Dropdown.Root>
                  </div>
                );
              },
            }),
          ]
        : []),
    ],
    [
      t,
      enumTranslations,
      actions,
      folderPaths,
      folderDefaultPriorityTooltip,
      knowledgePriorityHeaderTooltip,
      canUseAutoUpdate,
      isManagedSource,
      isChatMode,
      websiteHierarchyLabels,
    ],
  );
}
